import { Fragment, h } from "preact";
import { useDispatch, useSelector } from "react-redux";
import { useContext, useEffect, useState } from "preact/hooks";
import sortBy from "lodash/sortBy";

import Alert from "../components/Alert.jsx";
import AppHeader from "../components/AppHeader.jsx";
import AppLayout from "../components/AppLayout.jsx";
import CandidateItem from "../components/companies/CandidateItem.jsx";
import FilterBar from "../components/FilterBar.jsx";
import RelocationCasesFitler from "../components/RelocationCasesFilter.jsx";
import Hint from "../components/HintContainer.jsx";

import i18n from "../i18n.js";
import { renderHtml } from "../helpers.js";
import {
  selectCompany,
  selectCompanyUsers,
  selectAreCompanyUsersLoaded,
  setLoading,
  selectCompanyUsersLoadingError,
  fetchCompanyUsers,
  selectCanSeeTalentPool,
} from "../store/index";
import EmptyState from "../components/EmptyState.jsx";
import Button from "../components/Button.jsx";
import AddCaseDialog from "../components/AddCaseDialog.jsx";
import { PreviewModeContext } from "../contexts.js";
import usePreviewClickHandler from "../hooks/usePreviewClickHandler.js";

export default function RelocationCasesPage() {
  const dispatch = useDispatch();
  const previewMode = useContext(PreviewModeContext);
  const company = useSelector(selectCompany);
  const users = useSelector(selectCompanyUsers);
  const usersLoaded = useSelector(selectAreCompanyUsersLoaded);
  const usersLoadingError = useSelector(selectCompanyUsersLoadingError);
  const selectedFilter = useSelector((state) => state.ui.relocationCasesFitler);
  const [filteredItems, setFilteredItems] = useState(users);
  const [showAddCaseModal, setShowAddCaseModal] = useState(false);

  function handleAddCaseClick(event) {
    event.preventDefault();
    if (previewMode) {
      window.Location.href = "/preview/registration";
      return;
    }
    setShowAddCaseModal(true);
  }

  function handleModalClose() {
    setShowAddCaseModal(false);
  }

  const { handleClickWhenLoggedIn } = usePreviewClickHandler(
    selectCanSeeTalentPool
  );

  useEffect(async () => {
    if (previewMode) return;

    // if (!usersLoaded) {
    dispatch(setLoading(true));
    await dispatch(fetchCompanyUsers());
    dispatch(setLoading(false));
    // }
  }, []);

  useEffect(() => {
    setFilteredItems(
      users.filter((item) => {
        // FIXME: Not really optimized but good enough for now
        const numTasks = item.tasks.length || 1;
        const numDoneTasks = item.tasks.filter((task) => task.done).length;

        if (selectedFilter === "ongoing") {
          return numDoneTasks < numTasks;
        } else if (selectedFilter === "completed") {
          return numDoneTasks === numTasks;
        } else {
          return true;
        }
      })
    );
  }, [selectedFilter, users]);

  if (usersLoadingError) {
    return (
      <AppLayout title={i18n.t("pages.titles.relocation_cases")}>
        <Alert error>{i18n.t("errors.could_not_load_company_users")}</Alert>
      </AppLayout>
    );
  }

  let emptyState = null;
  if (filteredItems.length === 0) {
    if (selectedFilter) {
      emptyState = (
        <EmptyState
          icon="filtered_cases_empty"
          title={i18n.t("pages.relocation_cases.filter_empty.title")}
          description={i18n.t(
            "pages.relocation_cases.filter_empty.description"
          )}
        />
      );
    } else {
      emptyState = (
        <Fragment>
          <EmptyState
            icon="cases_empty"
            title={i18n.t("pages.relocation_cases.cases_empty.title")}
            description={i18n.t(
              "pages.relocation_cases.cases_empty.description"
            )}
          >
            <Button
              iconFront="add"
              onClick={handleClickWhenLoggedIn(handleAddCaseClick)}
            >
              {i18n.t("pages.relocation_cases.cases_empty.action")}
            </Button>
          </EmptyState>

          {showAddCaseModal && <AddCaseDialog onClose={handleModalClose} />}
        </Fragment>
      );
    }
  }

  return (
    <AppLayout
      header={
        <AppHeader
          title={i18n.t("pages.titles.relocation_cases")}
          filterBar={
            <FilterBar>
              <RelocationCasesFitler />
              <Button
                filterButton
                iconFront="add"
                onClick={handleClickWhenLoggedIn(handleAddCaseClick)}
              >
                {i18n.t("pages.relocation_cases.cases_empty.action")}
              </Button>
            </FilterBar>
          }
        />
      }
      headerWithFilter
    >
      <section class="screenPadding__inner">
        {emptyState ? (
          emptyState
        ) : usersLoaded && users.length === 0 ? (
          <Alert>
            {renderHtml(
              i18n.t("pages.relocation_cases.no_users", {
                code: company.invitationCode,
              })
            )}
          </Alert>
        ) : (
          <Fragment>
            <Hint />
            {sortBy(filteredItems, (u) => u.name).map((user) => (
              <CandidateItem user={user} />
            ))}
          </Fragment>
        )}
      </section>
      {showAddCaseModal && <AddCaseDialog onClose={handleModalClose} />}
    </AppLayout>
  );
}
